<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!submitting"
    :show-header="false"
    :style="{ width: '440px', maxWidth: '95vw' }"
    :content-style="{ padding: '0', borderRadius: '16px' }"
    :pt="{ root: { class: 'billing-confirm-root' } }"
    @update:visible="onVisibleChange"
  >
    <div class="billing-confirm">
      <div
        class="billing-confirm__icon"
        :class="`billing-confirm__icon--${variant}`"
      >
        <i :class="iconClass" />
      </div>

      <h3 class="billing-confirm__title">
        {{ title }}
      </h3>

      <p
        v-if="message"
        class="billing-confirm__message"
      >
        {{ message }}
      </p>

      <div class="billing-confirm__actions">
        <button
          type="button"
          class="billing-confirm__btn billing-confirm__btn--cancel"
          :disabled="submitting"
          @click="onCancel"
        >
          {{ cancelLabel }}
        </button>
        <button
          type="button"
          class="billing-confirm__btn"
          :class="`billing-confirm__btn--${variant}`"
          :disabled="submitting"
          @click="onConfirm"
        >
          <i
            v-if="submitting"
            class="fa-regular fa-spinner-third fa-spin"
          />
          <span>{{ confirmLabel }}</span>
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType,
} from 'vue';
import Dialog from 'primevue/dialog';

type Variant = 'primary' | 'danger';

export default defineComponent({
  name: 'BillingConfirmDialog',

  components: { Dialog },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    confirmLabel: {
      type: String,
      required: true,
    },
    cancelLabel: {
      type: String,
      required: true,
    },
    variant: {
      type: String as PropType<Variant>,
      default: 'primary',
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
  },

  emits: ['update:visible', 'confirm', 'cancel'],

  setup(props, { emit }) {
    const iconClass = computed(() => {
      if (props.icon) return props.icon;
      return props.variant === 'danger'
        ? 'fa-regular fa-triangle-exclamation'
        : 'fa-regular fa-circle-question';
    });

    const onConfirm = () => {
      if (props.submitting) return;
      emit('confirm');
    };

    const onCancel = () => {
      if (props.submitting) return;
      emit('cancel');
      emit('update:visible', false);
    };

    const onVisibleChange = (v: boolean) => {
      if (!v && !props.submitting) {
        emit('cancel');
        emit('update:visible', false);
      }
    };

    return {
      iconClass,
      onConfirm,
      onCancel,
      onVisibleChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.billing-confirm {
  padding: 28px 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    font-size: 1.5rem;
    animation: billing-confirm-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1);

    &--primary {
      background: linear-gradient(135deg, rgba(148, 200, 64, 0.16) 0%, rgba(148, 200, 64, 0.04) 100%);
      color: $brand-color-primary;
    }

    &--danger {
      background: #fee2e2;
      color: #dc2626;
    }
  }

  &__title {
    font-size: 1.0625rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 8px;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }

  &__message {
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.5;
    margin: 0 0 24px;

    // Allow the {brand} ···· {last4} portion to wrap cleanly.
    word-break: break-word;
  }

  &__actions {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  &__btn {
    flex: 1;
    padding: 10px 18px;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 160ms ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--cancel {
      background: #fff;
      border-color: #e2e8f0;
      color: #475569;

      &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #0f172a;
      }
    }

    &--primary {
      background: $brand-color-primary;
      color: #fff;
      box-shadow: 0 4px 12px -3px rgba(148, 200, 64, 0.45);

      &:hover:not(:disabled) {
        background: darken($brand-color-primary, 5%);
        transform: translateY(-1px);
        box-shadow: 0 6px 16px -3px rgba(148, 200, 64, 0.55);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    }

    &--danger {
      background: #dc2626;
      color: #fff;
      box-shadow: 0 4px 12px -3px rgba(220, 38, 38, 0.45);

      &:hover:not(:disabled) {
        background: #b91c1c;
        transform: translateY(-1px);
        box-shadow: 0 6px 16px -3px rgba(220, 38, 38, 0.55);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    }
  }
}

@keyframes billing-confirm-pop {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>

<style lang="scss">
.billing-confirm-root.p-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px -24px rgba(15, 23, 42, 0.32);

  .p-dialog-content {
    padding: 0 !important;
    border-radius: 0;
  }
}
</style>
